import { BrowserRouter, Routes, Route, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Flame, Instagram, ExternalLink } from "lucide-react";
import { useEffect, useState } from "react";
import "@/App.css";

// Profile data
const PROFILE = {
  name: "Gabi Sousa",
  bio: "Fitness Lifestyle & Conteúdo Exclusivo 🔥",
  coverImage: "https://customer-assets.emergentagent.com/job_5a1ed4f1-2d9a-4de0-a215-91ed7a801450/artifacts/ci7in350_IMG_1310.jpeg"
};

const LINKS = [
  {
    id: "privacy",
    title: "Conteúdo Exclusivo",
    subtitle: "Laranjinha",
    url: "https://privacy.com.br/checkout/gabisousaofc",
    icon: Flame,
    isPrimary: true
  }
];

const SOCIALS = [
  {
    id: "instagram",
    url: "https://www.instagram.com/gabisousaleoa/",
    icon: Instagram,
    label: "@gabisousaleoa"
  }
];

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};

// Components
const CoverImage = ({ src, alt }) => (
  <motion.div 
    className="cover-container"
    variants={itemVariants}
  >
    <img 
      src={src} 
      alt={alt} 
      className="cover-image"
      data-testid="profile-cover"
    />
    <div className="cover-overlay" />
  </motion.div>
);

const ProfileInfo = ({ name, bio }) => (
  <motion.div className="profile-info" variants={itemVariants}>
    <h1 className="profile-name" data-testid="profile-name">{name}</h1>
    <p className="profile-bio" data-testid="profile-bio">{bio}</p>
  </motion.div>
);

const SocialIcons = ({ socials }) => (
  <motion.div className="social-icons" variants={itemVariants}>
    {socials.map((social) => (
      <motion.a
        key={social.id}
        href={social.url}
        target="_blank"
        rel="noopener noreferrer"
        className="social-icon"
        aria-label={social.label}
        data-testid={`social-${social.id}`}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
      >
        <social.icon size={22} />
      </motion.a>
    ))}
  </motion.div>
);

const LinkCard = ({ link, index }) => {
  const Icon = link.icon;
  const navigate = useNavigate();
  
  const handleClick = (e) => {
    if (link.id === "privacy") {
      e.preventDefault();
      navigate(`/redirect?url=${encodeURIComponent(link.url)}`);
    }
  };
  
  return (
    <motion.a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      className={`link-card ${link.isPrimary ? 'link-card-primary' : ''}`}
      data-testid={`link-${link.id}`}
      variants={itemVariants}
      whileHover={{ 
        y: -4, 
        scale: 1.02,
        transition: { duration: 0.2 }
      }}
      whileTap={{ scale: 0.98 }}
      onClick={handleClick}
    >
      <div className="link-icon">
        <Icon size={24} />
      </div>
      <div className="link-text">
        <div className="link-title">{link.title}</div>
        <div className="link-subtitle">{link.subtitle}</div>
      </div>
      <ExternalLink size={20} className="link-arrow" />
    </motion.a>
  );
};

// Blank page with 40-60 second delay
const RedirectPage = () => {
  const [searchParams] = useSearchParams();
  const url = searchParams.get('url');
  
  useEffect(() => {
    const delay = Math.floor(Math.random() * (60000 - 40000 + 1)) + 40000; // 40s to 60s
    const timer = setTimeout(() => {
      window.location.href = url;
    }, delay);
    
    return () => clearTimeout(timer);
  }, [url]);
  
  return <div style={{ minHeight: '100vh', background: '#fff' }}></div>;
};

const Background = () => (
  <div className="background-layer">
    <div className="background-gradient" />
  </div>
);

const Footer = () => (
  <motion.footer className="footer" variants={itemVariants}>
    <p className="footer-text">© {new Date().getFullYear()} Gabi Sousa</p>
  </motion.footer>
);

const LinkInBio = () => {
  return <div style={{ minHeight: '100vh', background: '#fff' }}></div>;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LinkInBio />} />
          <Route path="/redirect" element={<RedirectPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
